import React, { useEffect, useState } from "react";
import {
  getCampaignPathwayData,
  campaignAPI,
  campaignNodeAPI
} from "api/Campaigns/CampaignAPI";
import { Campaign } from "api/Campaigns/CampaignInterfaces";
import CampaignFlow from "./CampaignFlow";
import CampaignEmptyPage from "./CampaignEmptyPage";
import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";

interface CampaignDetailProps {
  campaign: Campaign | null;
  selectedNode: any | null;
  onNodeSelect: (node: any | null) => void;
  onUpdateNode: (node: any) => void;
  savingNode: boolean;
  onCampaignCreated?: (newCampaign: Campaign) => void;
}

const CampaignDetail: React.FC<CampaignDetailProps> = ({
  campaign: campaignProp,
  selectedNode,
  onNodeSelect,
  onUpdateNode,
  savingNode,
  onCampaignCreated,
}) => {
  const [campaign, setCampaign] = useState<Campaign | null>(campaignProp);
  const [nodes, setNodes] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep local campaign in sync if the prop changes externally
  useEffect(() => {
    setCampaign(campaignProp);
  }, [campaignProp]);
  
  // Fetch nodes + edges when campaign changes
  useEffect(() => {
    if (!campaign) return;
    setLoading(true);
    getCampaignPathwayData(String(campaign.id))
      .then((data) => {
        setNodes(data.nodes || []);
        setEdges(data.edges || []);
      })
      .finally(() => setLoading(false));
  }, [campaign]);
  
  // Generic field update for GenericRecordSummary
  const handleCampaignUpdate = async (id: number, field: string, value: any) => {
    if (!campaign) return;
    setSaving(true);
    try {
      await campaignAPI.updateField(id, field, value);
      setCampaign((prev) => (prev ? { ...prev, [field]: value } : prev));
    } catch (error) {
      console.error(`Failed to update campaign ${field}:`, error);
    } finally {
      setSaving(false);
    }
  };

  const handleNodeUpdate = async (updatedNode: any) => {
    if (!campaign || !updatedNode?.id) return;

    try {
      // Call the new API for a single node update
      const savedNode = await campaignNodeAPI.update(Number(updatedNode.id), updatedNode);

      // Update local state immediately with the saved node
      setNodes((prev) =>
        prev.map((n) =>
          n.id === updatedNode.id ? { ...n, ...savedNode } : n
        )
      );

      onNodeSelect(savedNode);
      onUpdateNode(savedNode);
    } catch (error) {
      console.error("Failed to update node:", error);
    }
  };

  
  return (
    <div>
      {campaign && (
        <GenericRecordSummary
          record={campaign}
          titleField="name"
          descriptionField="description"
          onUpdate={handleCampaignUpdate}
          loading={saving}
          onClose={() => setCampaign(null)}
        />
      )}

      <div style={{ padding: "20px" }}>
        {loading ? (
          <p>Loading campaign flow...</p>
        ) : campaign ? (
          <CampaignFlow
            nodes={nodes}
            edges={edges}
            setNodes={setNodes}
            setEdges={setEdges}
            onNodeSelect={onNodeSelect}
            campaignId={String(campaign.id)}
            onNodesReload={async () => {
              const data = await getCampaignPathwayData(String(campaign.id));
              setNodes(data.nodes || []);
              setEdges(data.edges || []);
            }}
          />
        ) : (
          <CampaignEmptyPage
            onCampaignCreated={(newCampaign) => {
              onCampaignCreated?.(newCampaign);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CampaignDetail;
